import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ActionIcon, Box, Button, Card, Container, Group, Select, Tabs, Text, Title,
} from '@mantine/core';
import '../components/Machines/CSS/machinesDetails.css';
import client from '../API/API';
import StatusMachines from '../containers/StatusMachines.jsx';
import KpiMachines from '../containers/KpiMachines.jsx';
import DetailsMachines from '../containers/DetailsMachines.jsx';
import ControlMachines from '../containers/ControlMachines.jsx';
import LoaderComponent from '../components/loader/LoaderComponent.jsx';
import { MdFullscreen } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

const allTabs = [
    { id: 5, value: 'details', tabname: 'Details' },
    { id: 6, value: 'kpi', tabname: 'KPI' },
    { id: 7, value: 'iostatus', tabname: 'Status' },
    { id: 8, value: 'control', tabname: 'Control' },
];

const MachinePage = () => {
    const [tabs, setTabs] = useState([]);
    const [machines, setMachines] = useState([]);
    const [selectedMachineId, setSelectedMachineId] = useState(null);
    const [activeMachineId, setActiveMachineId] = useState(null);
    const [fullScreen, setFullscreen] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const divref = useRef(null);

    const activeMachine = useMemo(
        () => machines.find((machine) => machine.machine_id === activeMachineId) || null,
        [machines, activeMachineId]
    );

    const machineOptions = useMemo(
        () => machines.map((machine) => ({
            value: machine.machine_id,
            label: machine.machine_name || machine.machine_id,
        })),
        [machines]
    );

    const enabledTabs = useMemo(
        () => allTabs.filter((tab) => tabs.includes(tab.id)),
        [tabs]
    );

    const handleFullScreen = () => {
        if (!document.fullscreenElement) {
            divref.current.requestFullscreen().then(() => {
                setFullscreen(true);
            });
        } else {
            document.exitFullscreen().then(() => {
                setFullscreen(false);
            });
        }
    };

    useEffect(() => {
        const loadPageData = async () => {
            setLoading(true);
            try {
                const [moduleResponse, machinesResponse] = await Promise.all([
                    client.get('/api/machine_module/'),
                    client.get('/api/machines/'),
                ]);

                setTabs(moduleResponse.data.sub_pages || []);
                setMachines(machinesResponse.data.machines || []);
            } catch (error) {
                console.error('Error fetching machine page data:', error);
                if (error.response?.status === 401) {
                    window.localStorage.clear();
                    navigate('/login');
                }
            } finally {
                setLoading(false);
            }
        };

        loadPageData();
    }, [navigate]);

    useEffect(() => {
        if (!selectedMachineId && machines.length > 0) {
            setSelectedMachineId(machines[0].machine_id);
        }
    }, [machines, selectedMachineId]);

    const handleOpenMachine = () => {
        setActiveMachineId(selectedMachineId);
    };

    return (
        <Container fluid ml={10} mt="0.3rem">
            <Box mt={20} p="1rem" pt={0}>
                <Group align="flex-end">
                    <Select
                        label="Machines"
                        placeholder="Select machine"
                        searchable
                        clearable
                        nothingFound="No options"
                        data={machineOptions}
                        value={selectedMachineId}
                        onChange={setSelectedMachineId}
                        style={{ minWidth: 260 }}
                    />
                    <Button
                        onClick={handleOpenMachine}
                        disabled={!selectedMachineId}
                    >
                        Get Machine
                    </Button>
                </Group>
            </Box>

            <Box mt={10}>
                {activeMachine ? (
                    <Tabs defaultValue={enabledTabs[0]?.value || 'iostatus'}>
                        <div ref={divref} style={{ backgroundColor: 'var(--color-bg)' }}>
                            {fullScreen && (
                                <Text fw={600} fz="xl" m="1rem" mt="lg">
                                    Machine: {activeMachine.machine_name || activeMachine.machine_id}
                                </Text>
                            )}
                            <Tabs.List style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Group>
                                    {enabledTabs.length > 0 ? (
                                        enabledTabs.map((tab) => (
                                            <Tabs.Tab key={tab.id} value={tab.value}>
                                                {tab.tabname}
                                            </Tabs.Tab>
                                        ))
                                    ) : (
                                        <LoaderComponent />
                                    )}
                                </Group>
                                <ActionIcon onClick={handleFullScreen} right="2rem">
                                    <MdFullscreen size="2rem" />
                                </ActionIcon>
                            </Tabs.List>

                            <Card width="100%" h="100%" className="card">
                                <Tabs.Panel value="details" pt="xs">
                                    <DetailsMachines nodeid={activeMachine.id} machineid={activeMachine.machine_id} />
                                </Tabs.Panel>
                                <Tabs.Panel value="kpi" pt="xs">
                                    <KpiMachines
                                        nodeid={activeMachine.id}
                                        machineid={activeMachine.machine_id}
                                        screensize={fullScreen}
                                    />
                                </Tabs.Panel>
                                <Tabs.Panel value="iostatus" pt="xs">
                                    <StatusMachines nodeid={activeMachine.id} machineid={activeMachine.machine_id} />
                                </Tabs.Panel>
                                <Tabs.Panel value="control" pt="xs">
                                    <ControlMachines nodeid={activeMachine.id} machineid={activeMachine.machine_id} />
                                </Tabs.Panel>
                            </Card>
                        </div>
                    </Tabs>
                ) : loading ? (
                    <LoaderComponent />
                ) : machines.length === 0 ? (
                    <Box mt={20}>
                        <Title color="var(--color-bold-text)" size="1.1rem" weight={500}>
                            No machines assigned
                        </Title>
                    </Box>
                ) : null}
            </Box>
        </Container>
    );
};

export default MachinePage;
